import React, { useEffect, useState } from 'react';
import { autorun } from 'mobx';
import { isMobile } from 'react-device-detect';
import { observer } from 'mobx-react-lite';
import styled, { useTheme } from 'styled-components';
import { ReactComponent as EditSVG } from 'assets/images/dashboard/edit.svg';
import { ReactComponent as InvitorSVG } from 'assets/images/dashboard/invitor.svg';
import { ReactComponent as ArrowSVG } from 'assets/images/dashboard/arrow.svg';
import { RowBetween } from 'components/Row';
import { AutoColumn } from 'components/Column';
import Button, { default as CButton } from 'components/Button';
import { Button as MuiButton } from '@mui/material';
import Copy from 'components/Copy';
import { UserGecStatsV2 } from 'gql-ops/graph/__generated__/request';
import useCapsuleMethods from 'hooks/useCapsuleMethods';
import user from 'stores/user';
import { TYPE } from 'theme';
import { Card, Tag } from '../styled';
import EditNameModal from './EditNameModal';
import Tooltip from 'components/TooltipInfo';
import { ArrowIcon } from 'components/Icons';
import InviteCodeModal from './InviteCodeModal';
import { lighten } from 'polished';
import { GradientBorderBox } from 'components/styled/Box';
import Season4 from './Season4';
import Season3 from './Season3';
import Season2 from './Season2';
import Season1 from './Season1';

interface ThemeType {
    c: {
        primary01: string;
        primary02: string;
        linearGradientBg: string;
        // ... other theme colors
    };
}

const Wrapper = styled(Card)`
    padding: 45px 30px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        padding: 20px;
        margin-bottom: 20px;
    `}
    ${({ theme }) => theme.mediaWidth.upToExtraSmall`
        padding: 10px;
        padding-top: 20px;
    `}
`;

const OverviewBoard = styled.div`
    display: flex;
    gap: 30px;
    flex-wrap: wrap;
`;

const UserWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

const Avatar = styled.div`
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background-color: #452460;
    background-size: contain;

    ${({ theme }) => theme.mediaWidth.upToSmall`
        width: 32px;
        height: 32px;
    `};
`;

const YourInvitorWrapper = styled.div<{ theme: ThemeType }>`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 10px;
    flex-shrink: 0;
    font-size: 12px;
    background: ${({ theme }) => theme.c.primary02};
    border-radius: 4px;
    height: 32px;
    color: #646058;
    width: 100%;
    padding: 0 15px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
      padding: 0 8px;
      gap: 2px;
      font-size: 10px;
    `};
`;

const BindInviteWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    div {
        height: 32px;
        padding: 8px;
        padding-left: 14px;
        padding-right: 30px;
        color: #fff;
        font-size: 12px;
        border-radius: 4px;
        cursor: pointer;
        ${({ theme }) => theme.mediaWidth.upToSmall`
            width: auto;
        `};
        &:hover {
            opacity: 0.8;
        }
    }

    button {
        position: absolute;
        right: 6px;
        top: 50%;
        transform: translateY(-50%);
        background: ${({ theme }) => theme.c.primary02};
        border-radius: 6px;
        width: 28px !important;
        height: 20px !important;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        min-width: 28px !important;

        &:hover,
        &:focus {
            background: ${(props) => lighten(0.2, props.theme.c.primary02)};
        }

        svg {
            width: 8px;
            height: 8px;
        }
    }
`;

const InviteRow = styled.div`
    background: #121212;
    padding: 20px;
    margin-bottom: 30px;
    border-radius: 8px;
    display: grid;
    grid-template-columns: 1fr 1fr 200px;
    gap: 20px;
    ${({ theme }) => theme.mediaWidth.upToSmall`
        grid-template-columns: 1fr;
        background: unset;
        padding: 0;
        gap: 10px;
        margin-bottom: 20px;
    `};
`;

const StyledArrowIcon = styled(ArrowSVG)`
    width: 24px;
    height: 24px;
    cursor: pointer;
    transition: transform 0.2s ease;
    &:hover {
        opacity: 0.8;
    }
    &.up {
        transform: rotate(-180deg);
    }
`;

function maskAddress(address?: string) {
    if (!address) return 'N/A';

    return address.slice(0, 6) + '...' + address.slice(-4);
}

function formatDate(isoDateString: string | number) {
    if (!isoDateString) {
        return '';
    }
    const date = new Date(isoDateString);

    // Get year, month, and day
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    // Get hours and minutes
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    // Combine into desired format
    return `${year}-${month}-${day} ${hours}:${minutes} (UTC)`;
}

const Overview = () => {
    const theme = useTheme() as ThemeType;
    const [editNameModalOpen, setEditNameModalOpen] = useState(false);
    const [inviteCodeModalOpen, setInviteCodeModalOpen] = useState(false);
    const [showHistorySeasons, setShowHistorySeasons] = useState(false);

    const [gecStatsV2, setGecStatsV2] = useState<UserGecStatsV2>({
        address: '',
        season1Stats: {
            totalGEC: '0',
            totalPoint: '0',
        },
        season2Stats: {
            basicPoint: '0',
            hbNftBoost: '0',
            invitationBoost: '0',
        },
        season3Stats: {
            invitationPoint: '0',
            onChainPoint: '0',
            socialPoint: '0',
        },
        season4Stats: {
            totalEarnedPoints: '0',
            totalConsumedPoints: '0',
        },
        updatedAt: '',
    } as UserGecStatsV2);

    const { capsuleMeta, isMinted, tokenId } = useCapsuleMethods();

    const onCloseEditName = () => {
        setEditNameModalOpen(false);
    };

    const onEditUserName = () => {
        if (isMinted) {
            setEditNameModalOpen(true);
        }
    };

    const fetchGECStatsV2 = async () => {
        const userGECStatsV2 = await user.getUserGECStatsV2();
        if (userGECStatsV2) {
            setGecStatsV2(userGECStatsV2);
        }
    };

    useEffect(
        () =>
            autorun(() => {
                fetchGECStatsV2();
            }),
        [],
    );

    const getInviteLink = () => {
        if (!user.opsUser?.inviteCode) return '';
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `${origin}/invite/${user.opsUser.inviteCode}`;
    };

    const onCloseInviteCodeModal = () => {
        setInviteCodeModalOpen(false);
    };

    const getInvitationPercentage = () => {
        if (!user.opsUser?.completedInviteCount) return 0;
        if (user.opsUser?.completedInviteCount <= 10) {
            return 10;
        } else if (user.opsUser?.completedInviteCount <= 20) {
            return 15;
        } else if (user.opsUser?.completedInviteCount <= 30) {
            return 20;
        } else {
            return 25;
        }
    };

    return (
        <Wrapper>
            <RowBetween
                style={{
                    marginBottom: '40px',
                    ...(isMobile
                        ? {
                              flexDirection: 'column',
                              flexWrap: 'wrap',
                              alignItems: 'flex-start',
                              marginBottom: '20px',
                              gap: '10px',
                          }
                        : {}),
                }}
            >
                <AutoColumn>
                    <TYPE.main fontSize={24} fontWeight={700} mobileFz={18}>
                        Overview
                    </TYPE.main>
                    <TYPE.desc fontSize={14}>Data last updated {formatDate(gecStatsV2?.updatedAt)}</TYPE.desc>
                </AutoColumn>
                <UserWrapper>
                    <Avatar
                        style={{
                            ...(capsuleMeta?.image || user.opsUser?.avatar
                                ? {
                                      backgroundImage: `url(${capsuleMeta?.image || user.opsUser?.avatar})`,
                                  }
                                : {}),
                        }}
                    />
                    <AutoColumn justify="start">
                        <TYPE.main
                            fontSize={20}
                            mobileFz={14}
                            style={{
                                minWidth: '100px',
                            }}
                        >
                            {user.opsUser?.name || 'User Name'}
                        </TYPE.main>
                        <RowBetween
                            style={{
                                justifyContent: 'flex-start',
                                gap: '4px',
                            }}
                        >
                            <TYPE.desc fontSize={14} mobileFz={12}>
                                {maskAddress(user.address)}
                            </TYPE.desc>
                            <Copy text={user.address} />
                        </RowBetween>
                        {tokenId ? <Tag>#{tokenId}</Tag> : null}
                    </AutoColumn>
                    {!!isMinted ? (
                        <CButton type="unstyled" size="unset" onClick={onEditUserName} style={{ padding: 0 }}>
                            <EditSVG width="15px" />
                        </CButton>
                    ) : (
                        <Tooltip title="You need to mint Capsule NFT firstly.">
                            <EditSVG width="15px" style={{ cursor: 'not-allowed', opacity: 0.5 }} />
                        </Tooltip>
                    )}
                </UserWrapper>
                <EditNameModal isOpen={editNameModalOpen} onClose={onCloseEditName} />
            </RowBetween>
            <InviteRow>
                <Copy
                    text={getInviteLink()}
                    render={(onCopy) => (
                        <Button
                            style={{
                                backgroundColor: theme.c.primary02,
                                borderRadius: '4px',
                                height: '32px',
                                padding: isMobile ? '0 8px' : '0 14px',
                                width: '100%',
                                minWidth: isMobile ? '80px' : 'fit-content',
                            }}
                            onClick={onCopy}
                        >
                            <TYPE.desc
                                fontSize={12}
                                style={{
                                    textTransform: 'none',
                                    ...(isMobile
                                        ? {
                                              fontWeight: 600,
                                          }
                                        : {}),
                                }}
                            >
                                Invite link
                            </TYPE.desc>
                        </Button>
                    )}
                />
                <Copy
                    style={{ width: isMobile ? '100%' : 'auto' }}
                    text={user.opsUser?.inviteCode ?? ''}
                    render={(onCopy) => (
                        <Button
                            style={{
                                background: theme.c.linearGradientBg,
                                borderRadius: '4px',
                                height: '32px',
                                paddingLeft: '14px',
                                paddingRight: '14px',
                                minWidth: isMobile ? '80px' : 'fit-content',
                                width: '100%',
                            }}
                            onClick={onCopy}
                        >
                            <TYPE.desc
                                fontSize={12}
                                style={{
                                    textTransform: 'none',
                                }}
                            >
                                Invite code:
                            </TYPE.desc>
                            <TYPE.desc fontSize={12}>{user.opsUser?.inviteCode}</TYPE.desc>
                        </Button>
                    )}
                />
                {user.opsUser?.invitedBy ? (
                    <YourInvitorWrapper style={{ flex: 1 }}>
                        <InvitorSVG style={{ flexShrink: 0, width: isMobile ? '12px' : '16px' }} />
                        <TYPE.desc fontSize={12}>
                            Your Invitor: {maskAddress(user.opsUser?.invitedBy?.walletAddress)}
                        </TYPE.desc>
                    </YourInvitorWrapper>
                ) : (
                    <GradientBorderBox style={{ padding: 0, borderRadius: '4px' }}>
                        <BindInviteWrapper
                            onClick={() => setInviteCodeModalOpen(true)}
                            style={{ flex: isMobile ? 1 : 'none' }}
                        >
                            <TYPE.desc fontSize={12} style={{ width: '100%' }}>
                                Invitation Code
                            </TYPE.desc>
                            <MuiButton>
                                <ArrowIcon color="#fff" width={8} height={8} />
                            </MuiButton>
                        </BindInviteWrapper>
                    </GradientBorderBox>
                )}
            </InviteRow>
            <OverviewBoard>
                <Season4 gecStatsV2={gecStatsV2} />
                <RowBetween>
                    <TYPE.main fontSize={16} fontWeight={700} mobileFz={14}>
                        History Seasons
                    </TYPE.main>
                    <StyledArrowIcon
                        className={showHistorySeasons ? 'up' : ''}
                        onClick={() => setShowHistorySeasons(!showHistorySeasons)}
                    />
                </RowBetween>
                {showHistorySeasons && (
                    <>
                        <Season3 gecStatsV2={gecStatsV2} />
                        <Season2 gecStatsV2={gecStatsV2} getInvitationPercentage={getInvitationPercentage} />
                        <Season1 gecStatsV2={gecStatsV2} />
                    </>
                )}
            </OverviewBoard>
            <InviteCodeModal isOpen={inviteCodeModalOpen} onClose={onCloseInviteCodeModal} />
        </Wrapper>
    );
};

export default observer(Overview);
